<template>
  <span
    class="ua"
    :class="[`ua--${tone}`, { 'ua--img': showImage }]"
    :style="{ '--ua-size': `${size}px` }"
    :title="title || undefined"
    :aria-label="ariaLabel || undefined"
    :aria-hidden="ariaLabel ? undefined : 'true'"
    :data-test="dataTest"
  >
    <img
      v-if="showImage"
      :src="src as string"
      class="ua-img"
      alt=""
      loading="lazy"
      decoding="async"
      referrerpolicy="no-referrer"
      @error="failed = true"
    />
    <span v-else class="ua-txt">{{ letters }}</span>
  </span>
</template>

<script setup lang="ts">
/*
AB#21839 — a person's face where the portal shows their name. The picture
comes from Microsoft 365 (staff who sign in with Microsoft); everyone else,
and anyone whose picture fails to load, gets the initials disc exactly as
before. Same disc, same accent, so a row of people reads as one set
whether or not each has a picture.

`src` is the versioned avatar_url the API hands out with every user; the
browser caches it for a year, and a changed picture is a new URL.
*/
import { computed, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    /** The person's name; initials are taken from it when there is no picture. */
    name?: string | null;
    /** Precomputed initials, when the caller already has them (desk messages do). */
    initials?: string | null;
    /** The versioned avatar_url from the API, or null for initials. */
    src?: string | null;
    /** Disc diameter in px. 44 header of a card, 32 sidebar, 22 list row. */
    size?: number;
    /** accent (default) | soft — a quieter disc for dense lists. */
    tone?: 'accent' | 'soft';
    title?: string;
    ariaLabel?: string;
    dataTest?: string;
  }>(),
  {
    name: '',
    initials: null,
    src: null,
    size: 32,
    tone: 'accent',
    title: '',
    ariaLabel: '',
    dataTest: 'user-avatar',
  }
);

const failed = ref(false);
watch(
  () => props.src,
  () => (failed.value = false)
);

const showImage = computed(() => Boolean(props.src) && !failed.value);

const letters = computed(() => {
  if (props.initials) return props.initials;
  const name = (props.name ?? '').trim();
  if (name === '') return '·';
  const parts = name.split(/\s+/).filter(Boolean);
  const l = (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '');
  return l.toUpperCase() || '·';
});
</script>

<style scoped>
.ua {
  width: var(--ua-size, 32px);
  height: var(--ua-size, 32px);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  overflow: hidden;
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  font-size: calc(var(--ua-size, 32px) * 0.38);
  letter-spacing: 0.02em;
  line-height: 1;
  user-select: none;
}
.ua--soft {
  background: var(--accent-soft);
  color: var(--accent);
}
.ua--img {
  background: var(--surface-3);
}
.ua-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
</style>
